import { Box, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSocketInstanceContext } from 'context/socketInstanceContext';
import { bonoBotState } from 'store/bonoBot/bonoBot.slice';
import { CharityType } from 'store/causes/types';
import { useAppSelector } from 'store/hooks';

const DonationPlan = () => {
   const { t } = useTranslation();
   const { selectedCauses, selectedCharities, donationAmount } = useAppSelector(bonoBotState);

   const [charitiesToShow, setCharitiesToShow] = useState<(CharityType | { emptyId: string })[]>([]);

   const getCharitiesToMap = (selected: CharityType[]) => {
      switch (selected.length) {
         case 1:
            return [...selected, { emptyId: 'empty-charity-1' }, { emptyId: 'empty-charity-2' }];
         case 2:
            return [...selected, { emptyId: 'empty-charity-1' }];
         case 3:
            return selected;
         default:
            return [{ emptyId: 'empty-charity-1' }, { emptyId: 'empty-charity-2' }, { emptyId: 'empty-charity-3' }];
      }
   };

   useEffect(() => {
      const ch = getCharitiesToMap(selectedCharities);
      setCharitiesToShow(ch);
   }, [selectedCharities]);

   return (
      <Box
         sx={{
            py: 1.5,
            px: 2.5,
            display: 'flex',
            flexDirection: 'column',
            height: '112px',
            backgroundColor: '#EBE9E5',
            width: '100%',
         }}
      >
         <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
               <Typography sx={{ fontSize: '16px', lineHeight: '17px', fontWeight: 700, color: '#333333' }}>
                  {t('bonoBot.donationPlan.title')}
               </Typography>
               <Box
                  sx={{
                     ml: '4px',
                     borderRadius: '4px',
                     py: '2px',
                     px: '4px',
                     backgroundColor: selectedCauses?.[0]?.impactHashtag ? '#EFCAC9' : '#F7F7F7',
                  }}
               >
                  <Typography
                     sx={{
                        fontSize: '12px',
                        lineHeight: '14px',
                        fontWeight: 600,
                        color: '#333333',
                     }}
                  >
                     {selectedCauses?.[0]?.impactHashtag ?? t('bonoBot.donationPlan.causePlaceholder')}
                  </Typography>
               </Box>
            </Box>
            {!!donationAmount && (
               <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                  <Typography sx={{ fontSize: '16px', lineHeight: '17px', fontWeight: 700, color: '#333333' }}>
                     ${donationAmount}/
                  </Typography>
                  <Typography sx={{ fontSize: '10px', lineHeight: '11px', color: '#333333', fontWeight: 600 }}>
                     month
                  </Typography>
               </Box>
            )}
         </Box>

         <Box sx={{ mt: 1.25, display: 'flex', flexDirection: 'column' }}>
            <Typography
               sx={{
                  fontSize: '12px',
                  lineHeight: '14px',
                  fontWeight: 700,
                  color: '#666666',
                  textTransform: 'uppercase',
                  mb: 0.75,
               }}
            >
               {t('bonoBot.donationPlan.subtitle')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'row', columnGap: '4px' }}>
               {charitiesToShow.map((ch) => {
                  if ('emptyId' in ch) {
                     return (
                        <Box
                           key={ch.emptyId}
                           sx={{
                              borderRadius: '4px',
                              backgroundColor: '#FFFFFF',
                              minHeight: '40px',
                              height: '40px',
                              width: '40px',
                              minWidth: '40px',
                              border: '1px dashed #CCCCCC',
                           }}
                        />
                     );
                  }
                  return (
                     <Box
                        key={ch.id}
                        sx={{
                           borderRadius: '4px',
                           minHeight: '40px',
                           maxHeight: '40px',
                           minWidth: '40px',
                           maxWidth: '40px',
                           overflow: 'hidden',
                           display: 'flex',
                           alignItems: 'center',
                           justifyContent: 'center',
                           border: '1px solid #E6E6E6',
                        }}
                     >
                        <img
                           alt={ch.title}
                           src={ch.icon}
                           style={{
                              height: '100%',
                              width: '100%',
                              objectFit: 'cover',
                           }}
                        />
                     </Box>
                  );
               })}
            </Box>
         </Box>
      </Box>
   );
};

export default DonationPlan;
